/**
 * Which backend a build logs through, parsed from an untrusted string.
 *
 * Two call sites need this answer and they must agree: the app's normal
 * logging composition (`apps/retells/src/logging/backend.ts`, reading
 * `import.meta.env.VITE_LOG_BACKEND`) and the early error capture that runs
 * before it, which has to pick a backend of its own to hold module-eval and
 * root-render failures. A parser they both import is what keeps a malformed
 * value from meaning one thing in one of them and something else in the
 * other.
 *
 * Pure and environment-agnostic like the rest of this package (see
 * core-fields.ts): it reads no env of its own, it just classifies whatever
 * string it is handed.
 */

/** The backends a build can be configured to log through. */
export type LogBackendChoice = "console" | "axiom" | "both";

const CHOICES: readonly LogBackendChoice[] = ["console", "axiom", "both"];

/**
 * Map a configured value onto a backend choice, falling back to `"console"`.
 *
 * `"console"` is the fallback rather than `"both"` deliberately: an
 * unrecognized value is a misconfiguration, and a misconfiguration must not
 * be the thing that starts POSTing events to a network sink. Console-only
 * degrades to the zero-infrastructure default a fresh clone already runs
 * with, which is also why the unset case (`undefined`) lands here.
 *
 * The caller is the right place to *notice* the fallback — a config value
 * that was ignored deserves a one-time `console.warn` naming it — but not
 * the right place to decide what it means.
 */
export function parseLogBackend(value: unknown): LogBackendChoice {
  return CHOICES.find((choice) => choice === value) ?? "console";
}
